import { Variant, CountryCode, ProductCodeKey, BoxVariants, ProductVariantPrice } from '@dayetopia/library';
import { PAD_STACK_PRODUCT_IDS } from '@constants';
import { CurrencyObj, PadSizeType, PadsBoxStackState, PadsBoxState } from '@contracts';
import { getSubscriptionPlans } from './getSubscriptionPlans';
import { logger } from './logger';
import {
  getAllPadTypes,
  getLinerVariantKey,
  getPadVariantKeys,
  getRegularVariantKey,
  getSuperPlusVariantKey,
  getSuperVariantKey,
  isPadsStackBox
} from './pads';
import { getDiscountedPrice, getProductPrice, getPadStackPrices, resolvePrice } from './price';
import { getProductPrices } from './prices';
import { getDiscountPercent } from './subscriptions';

export async function getLinerPadsPrice(country: CountryCode, currency: CurrencyObj) {
  const linerVariant = { [getLinerVariantKey(country)]: 1 } as Variant;

  const linerPadPrice = (await getBoxPrice(linerVariant, currency, country, ProductCodeKey.Pads)) || 0;
  return linerPadPrice;
}

export async function getRegularPadsPrice(country: CountryCode, currency: CurrencyObj) {
  const regularVariant = { [getRegularVariantKey(country)]: 1 } as Variant;

  const regularPadPrice = (await getBoxPrice(regularVariant, currency, country, ProductCodeKey.Pads)) || 0;
  return regularPadPrice;
}

export async function getSuperPadsPrice(country: CountryCode, currency: CurrencyObj) {
  const nightPadVariantPrice = { [getSuperVariantKey(country)]: 1 } as Variant;

  const nightPadPrice = (await getBoxPrice(nightPadVariantPrice, currency, country, ProductCodeKey.Pads)) || 0;
  return nightPadPrice;
}

export async function getSuperPlusPadsPrice(country: CountryCode, currency: CurrencyObj) {
  const nightPlusPadVariant = { [getSuperPlusVariantKey(country)]: 1 } as Variant;

  const nightPlusPadPrice = (await getBoxPrice(nightPlusPadVariant, currency, country, ProductCodeKey.Pads)) || 0;
  return nightPlusPadPrice;
}

export async function getPadsPrice(padsBoxStackState: PadsBoxStackState, padsBoxState: PadsBoxState, currency: CurrencyObj, country: CountryCode) {
  const finalPadsBoxState = isPadsStackBox(country)
    ? {
        [PAD_STACK_PRODUCT_IDS[PadSizeType.LINER]]: padsBoxStackState[PadSizeType.LINER],
        [PAD_STACK_PRODUCT_IDS[PadSizeType.REGULAR]]: padsBoxStackState[PadSizeType.REGULAR],
        [PAD_STACK_PRODUCT_IDS[PadSizeType.SUPER]]: padsBoxStackState[PadSizeType.SUPER],
        [PAD_STACK_PRODUCT_IDS[PadSizeType.SUPER_PLUS]]: padsBoxStackState[PadSizeType.SUPER_PLUS]
      }
    : padsBoxState;

  const productPrice = await getBoxPrice(finalPadsBoxState as Variant, currency, country, ProductCodeKey.Pads);

  return productPrice || 0;
}

export async function getMinPrice(productCode: ProductCodeKey, currency: CurrencyObj, country: CountryCode, variants?: Variant) {
  try {
    let maxDiscount = 0;
    let basePrice;
    let startingFrom = false;
    const plansProductCode = productCode === ProductCodeKey.VMSEssential ? ProductCodeKey.ScreeningKit : productCode;

    if (!plansProductCode) {
      return undefined;
    }

    const plans = await getSubscriptionPlans(plansProductCode);
    if (plans) {
      maxDiscount = plans.reduce((max: number, subscription) => Math.max(max, getDiscountPercent(subscription, country)), 0);
    }

    let finalVariants: Variant | undefined = variants;

    if (productCode === ProductCodeKey.TamponBox) {
      if (!finalVariants) {
        if (country === CountryCode.US) {
          finalVariants = { nakedregular: 18 } as Variant;
        } else {
          finalVariants = { nakedregular: 12 } as Variant;
        }
      }
      basePrice = getDiscountedPrice((await getBoxPrice(finalVariants, currency, country)) ?? -1, maxDiscount);
      startingFrom = true;
    } else if (productCode === ProductCodeKey.Pads) {
      if (isPadsStackBox(country)) {
        basePrice = getDiscountedPrice((await getProductPrice(ProductCodeKey.PadsRegular12, currency, country))?.price ?? -1, maxDiscount);
        startingFrom = true;
      } else {
        basePrice = getDiscountedPrice((await getProductPrice(ProductCodeKey.Pads, currency, country))?.price ?? -1, maxDiscount);
        startingFrom = true;
      }
    } else {
      basePrice = (await getProductPrice(productCode, currency, country)).price;
    }

    return {
      startingFrom,
      price: Number(basePrice?.toFixed(2))
    };
  } catch (e) {
    logger.error('Failed to fetch box prices', e);
    return undefined;
  }
}

export async function getPreconfiguredBoxesPrice(variantsDict: BoxVariants, currency: CurrencyObj, country: CountryCode) {
  if (!variantsDict) return null;

  const prices = await Promise.all(
    Object.entries(variantsDict)
      .filter(([, count]) => count > 0)
      .map(async ([variant, count]) => {
        const productPrice = await getProductPrice(variant, currency, country);
        return (productPrice?.price || 0) * count;
      })
  );

  return prices.reduce((sum, p) => sum + p, 0);
}

export async function getPreconfiguredTamponBoxPrices(currency: CurrencyObj, country: CountryCode) {
  const prices = {
    [ProductCodeKey.TamponBox9CR]: (await getProductPrice(ProductCodeKey.TamponBox9CR, currency, country))?.price || 0,
    [ProductCodeKey.TamponBox9CS]: (await getProductPrice(ProductCodeKey.TamponBox9CS, currency, country))?.price || 0,
    [ProductCodeKey.TamponBox18NR]: (await getProductPrice(ProductCodeKey.TamponBox18NR, currency, country))?.price || 0,
    [ProductCodeKey.TamponBox18NS]: (await getProductPrice(ProductCodeKey.TamponBox18NS, currency, country))?.price || 0,
    [ProductCodeKey.TamponBox9NR9CR]: (await getProductPrice(ProductCodeKey.TamponBox9NR9CR, currency, country))?.price || 0,
    [ProductCodeKey.TamponBox9NS9CS]: (await getProductPrice(ProductCodeKey.TamponBox9NS9CS, currency, country))?.price || 0,
    // Bundle product prices
    [ProductCodeKey.TamponBoxRetailBundleRegularFlow]:
      (await getProductPrice(ProductCodeKey.TamponBoxRetailBundleRegularFlow, currency, country))?.price || 0,
    [ProductCodeKey.TamponBoxRetailBundleRegularFlowMM]:
      (await getProductPrice(ProductCodeKey.TamponBoxRetailBundleRegularFlowMM, currency, country))?.price || 0,
    [ProductCodeKey.TamponBoxRetailBundleHeavyFlow]:
      (await getProductPrice(ProductCodeKey.TamponBoxRetailBundleHeavyFlow, currency, country))?.price || 0,
    [ProductCodeKey.TamponBoxRetailBundleHeavyFlowMM]:
      (await getProductPrice(ProductCodeKey.TamponBoxRetailBundleHeavyFlowMM, currency, country))?.price || 0
  };

  return prices;
}

export async function getBoxPrice(variantsDict: Variant, currency: CurrencyObj, country: CountryCode, boxProductCode = 'tampon_box') {
  if (!variantsDict) return null;
  return getPrice(variantsDict, boxProductCode);

  function calculateVATPrice(price: number, vatPercent: number): number {
    if (vatPercent && vatPercent > 0) {
      const vatAmount = price * (vatPercent / 100);
      return vatAmount;
    }
    return 0;
  }

  async function getPrice(variants: any, productCode: string) {
    let price = 0.0;
    const pricesData = await getProductPrices();
    const padSizeTypes = getAllPadTypes();
    const isPadsStack = isPadsStackBox(country);
    const padsStackPrices = isPadsStack ? await getPadStackPrices(variants, currency, country) : null;
    const padVariantKeys = getPadVariantKeys(country);

    if (pricesData) {
      const product = pricesData.find((itm: any) => itm.id === productCode);
      if (product) {
        const productVariants = (product as any).variants;
        const vatPercent = (product as any)?.vat[country] ?? 0;
        const variantsArray = Object.keys(variants).map((variant: any) => ({ variant, count: variants[variant] }));

        if (productVariants) {
          variantsArray.forEach(async (configurationItem) => {
            const isPadStackKeyIncluded = Object.values(padVariantKeys).includes(configurationItem.variant as ProductCodeKey);
            const isLegacyPadKeyIncluded = padSizeTypes.includes(configurationItem.variant as PadSizeType);

            if (isPadStackKeyIncluded && padsStackPrices) {
              price += padsStackPrices[configurationItem.variant as PadSizeType];
            } else if (
              configurationItem.variant !== 'initiationbox' &&
              configurationItem.variant !== 'mask' &&
              configurationItem.variant !== 'proviotics' &&
              (!isPadsStack || !isLegacyPadKeyIncluded)
            ) {
              const match = productVariants.find((variant: any) => variant.id === configurationItem.variant);

              if (match) {
                const matchPrice = match.price.find((variant: any) => variant.currency === currency.letterCode);
                if (matchPrice) {
                  // Use resolvePrice to apply country-specific amountOverride (e.g., Bulgaria EUR)
                  const baseAmount = resolvePrice(matchPrice as ProductVariantPrice, country);
                  const VATPrice = calculateVATPrice(baseAmount, vatPercent);
                  const finalItemPrice = (baseAmount + VATPrice)?.toFixed(2);
                  price += configurationItem.count * Number(finalItemPrice);
                }
              }
            }
          });
        }
      }
    }

    return price;
  }
}
